import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../utils/api";
import { getAccessToken, updateStoredSessionUser } from "../utils/auth";

const API = API_BASE_URL;

const ProfileMenu = ({ user, onUserUpdate, openRequestToken }) => {
  const [profileOpen, setProfileOpen] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [profileImageDataUrl, setProfileImageDataUrl] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileStatus, setProfileStatus] = useState("");

  const resolvedDisplayName =
    user?.displayName || user?.coachName || user?.username || "Coach";

  useEffect(() => {
    setDisplayName(resolvedDisplayName);
    setProfileImageDataUrl(user?.profileImageDataUrl || "");
    setNewPassword("");
    setConfirmPassword("");
    setProfileStatus("");
  }, [resolvedDisplayName, user?.profileImageDataUrl]);

  useEffect(() => {
    if (openRequestToken) {
      setProfileOpen(true);
    }
  }, [openRequestToken]);

  const avatarLabel = useMemo(() => {
    const text = (resolvedDisplayName || "").trim();
    if (!text) return "U";
    const parts = text.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
    return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
  }, [resolvedDisplayName]);

  const handleProfileImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setProfileStatus("Error: Please choose an image file.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const value = typeof reader.result === "string" ? reader.result : "";
      setProfileImageDataUrl(value);
      setProfileStatus("");
    };
    reader.onerror = () => {
      setProfileStatus("Error: Unable to read image file.");
    };
    reader.readAsDataURL(file);
  };

  const saveProfile = async () => {
    const token = getAccessToken();
    if (!token) {
      setProfileStatus("Error: Please log in again.");
      return;
    }

    const nextDisplayName = displayName.trim();
    if (!nextDisplayName) {
      setProfileStatus("Error: Display name is required.");
      return;
    }

    if (newPassword && newPassword.length < 8) {
      setProfileStatus("Error: Password must be at least 8 characters.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setProfileStatus("Error: Password confirmation does not match.");
      return;
    }

    const payload = {
      display_name: nextDisplayName,
      profile_image_data_url: profileImageDataUrl || "",
    };

    if (newPassword) payload.new_password = newPassword;

    setSavingProfile(true);
    setProfileStatus("");

    try {
      const response = await axios.patch(`${API}/auth/profile`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const updated = {
        displayName:
          response.data?.user?.display_name ||
          response.data?.user?.coach_name ||
          nextDisplayName,
        profileImageDataUrl: response.data?.user?.profile_image_data_url || "",
      };

      updateStoredSessionUser(updated);
      if (typeof onUserUpdate === "function") onUserUpdate(updated);

      setNewPassword("");
      setConfirmPassword("");
      setProfileStatus("Saved profile changes.");
    } catch (error) {
      const detail = error?.response?.data?.detail || error?.message;
      setProfileStatus(`Error: ${detail || "Unable to save profile."}`);
    } finally {
      setSavingProfile(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="profile-fab"
        onClick={() => setProfileOpen(true)}
        aria-label="Open profile"
      >
        {profileImageDataUrl ? (
          <img src={profileImageDataUrl} alt="Profile" className="profile-fab-image" />
        ) : (
          <span>{avatarLabel}</span>
        )}
      </button>

      {profileOpen ? (
        <div className="profile-modal-overlay" onClick={() => setProfileOpen(false)}>
          <div className="profile-modal-card" onClick={(event) => event.stopPropagation()}>
            <h3 className="profile-modal-title">My Profile</h3>
            <p className="profile-modal-subtitle">{user?.username}</p>

            <div className="profile-modal-avatar-wrap">
              {profileImageDataUrl ? (
                <img src={profileImageDataUrl} alt="Profile" className="profile-modal-avatar" />
              ) : (
                <div className="profile-modal-avatar profile-modal-avatar-fallback">{avatarLabel}</div>
              )}
            </div>

            <label className="profile-modal-label" htmlFor="profile-name-input">Display Name</label>
            <input
              id="profile-name-input"
              className="profile-modal-input"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              placeholder="Display name"
            />

            <label className="profile-modal-label" htmlFor="profile-password-input">New Password</label>
            <input
              id="profile-password-input"
              className="profile-modal-input"
              type="password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              placeholder="Leave blank to keep current password"
            />

            <label className="profile-modal-label" htmlFor="profile-confirm-input">Confirm Password</label>
            <input
              id="profile-confirm-input"
              className="profile-modal-input"
              type="password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              placeholder="Confirm new password"
            />

            <label className="profile-modal-label" htmlFor="profile-image-input">Mascot</label>
            <input
              id="profile-image-input"
              className="profile-modal-file"
              type="file"
              accept="image/*"
              onChange={handleProfileImageChange}
            />

            <button
              type="button"
              className="profile-modal-remove-btn"
              onClick={() => setProfileImageDataUrl("")}
            >
              Remove Mascot
            </button>

            {profileStatus ? <p className="profile-modal-status">{profileStatus}</p> : null}

            <div className="profile-modal-actions">
              <button
                type="button"
                className="profile-modal-btn"
                onClick={saveProfile}
                disabled={savingProfile}
              >
                {savingProfile ? "Saving…" : "Save"}
              </button>
              <button
                type="button"
                className="profile-modal-btn profile-modal-btn-secondary"
                onClick={() => setProfileOpen(false)}
                disabled={savingProfile}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default ProfileMenu;
